import { UserManager, WebStorageStateStore, InMemoryWebStorage } from 'oidc-client-ts';
import type { UserManagerSettings } from 'oidc-client-ts';
import { cognitoAuthority } from './authority';

/**
 * The Google popup for the add-a-login ceremony (W1.5). This is the single
 * most important technical correctness point in the whole ceremony: the
 * app's ONE signed-in session lives in `auth/config.ts`'s `oidcConfig`, whose
 * `userStore` is a `WebStorageStateStore` over `window.localStorage` with the
 * library's default key prefix (`"oidc."`). A second `UserManager` built with
 * the SAME `authority` + `client_id` + prefix + `localStorage` would compute
 * the exact same storage key (`oidc.user:<authority>:<client_id>`) — so the
 * instant the popup's sign-in completed, it would silently OVERWRITE the
 * live session's stored user. The live session's `origin_jti` would change
 * out from under it, and every step-up assurance tied to that session would
 * break, invisibly, with no error the user could act on.
 *
 * The fix is total isolation: BOTH the interaction-state store and the
 * resulting-user store use a fresh `InMemoryWebStorage()` instance (never
 * `window.localStorage`/`sessionStorage`) AND a distinct key prefix, so even
 * if a future refactor ever pointed one of them at real browser storage by
 * mistake, the prefix alone would still keep the two sessions' keys apart.
 * `client_id` can also be a wholly separate app client via
 * `VITE_COGNITO_LINK_CLIENT_ID` (contract: "ideally a separate app client");
 * it falls back to the main client id when that's unset.
 */
const LINK_POPUP_PREFIX = 'steward-link-popup.';

export function linkPopupClientId(): string {
  return import.meta.env.VITE_COGNITO_LINK_CLIENT_ID || import.meta.env.VITE_COGNITO_CLIENT_ID;
}

export function buildLinkPopupConfig(): UserManagerSettings {
  return {
    // Same pool as the main session by construction (authority.ts), which is what
    // makes the storage-key note above true.
    authority: cognitoAuthority(),
    client_id: linkPopupClientId(),
    redirect_uri: `${window.location.origin}/link-callback`,
    scope: 'openid email profile',
    response_type: 'code',
    // Own, isolated stores — see the module doc above. Never share these with
    // auth/config.ts's oidcConfig, and never point either at window.localStorage
    // or window.sessionStorage.
    userStore: new WebStorageStateStore({ store: new InMemoryWebStorage(), prefix: LINK_POPUP_PREFIX }),
    stateStore: new WebStorageStateStore({ store: new InMemoryWebStorage(), prefix: LINK_POPUP_PREFIX }),
    // Forces Cognito straight to the Google IdP (bypassing the Hosted UI
    // provider-choice screen — this popup only ever adds a Google login) and
    // asks Google itself to show the account chooser (W1.9) rather than
    // silently reusing whichever Google session is already active in this
    // browser — the whole point of "choose the Google account to add" (spec
    // §3 moment 2) is letting them pick a DIFFERENT one if they want to.
    // Whether Cognito's Hosted UI forwards `prompt` through to Google is
    // unconfirmed pending a live dev IdP (contract note) — this is a no-op,
    // not a regression, if it isn't forwarded.
    extraQueryParams: {
      identity_provider: 'Google',
      prompt: 'select_account',
    },
  };
}

export type LinkPopupOutcome =
  | { kind: 'success'; idToken: string }
  | { kind: 'blocked' }
  | { kind: 'cancelled' }
  | { kind: 'error'; message: string };

/**
 * Opens the Google popup and resolves once it completes, is closed without
 * finishing, is blocked by the browser, or `signal` is aborted (the modal's
 * own "Cancel" button while the popup is open). Must be called from a user
 * gesture (a click handler) — browsers reject `window.open` calls made
 * outside one, which is exactly the `blocked` case's most common real-world
 * cause.
 *
 * A fresh {@link UserManager} per call (never a module-level singleton): each
 * ceremony gets its own throwaway in-memory stores, so a cancelled or failed
 * attempt can never leave stale interaction state behind for the next try.
 */
export async function openGoogleLinkPopup(signal?: AbortSignal): Promise<LinkPopupOutcome> {
  const manager = new UserManager(buildLinkPopupConfig());
  try {
    const user = await manager.signinPopup({
      popupSignal: signal,
      // Also reject if the user closes the popup window by hand (the
      // `checkForPopupClosedInterval` poll in PopupWindow only raises the
      // abort event when this is set) — otherwise signinPopup() would hang
      // forever waiting for a message that will never arrive.
      popupAbortOnClose: true,
    });
    if (!user.id_token) return { kind: 'error', message: 'The sign-in window did not return an ID token.' };
    return { kind: 'success', idToken: user.id_token };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    // oidc-client-ts's PopupWindow: a null `window.open()` result (blocked)
    // surfaces as "Attempted to navigate on a disposed window"; a popup the
    // user closes before finishing (or our own abort signal) surfaces as
    // "Popup closed by user".
    if (/disposed window/i.test(message)) return { kind: 'blocked' };
    if (/closed by user/i.test(message)) return { kind: 'cancelled' };
    if (signal?.aborted) return { kind: 'cancelled' };
    return { kind: 'error', message };
  }
}
